import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { nameofFactory } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import TextField from "../../components/Editing/TextField";
import { pdApiUrl } from "../../utils/pdApi";
import axios, { AxiosResponse } from "axios";
import DropdownField from "../../components/Editing/DropdownField";
import { DropdownOption } from "../../utils/pdApiUtils";
import CheckboxField from "../../components/Editing/CheckboxField";

type StateProps = {
  editedLanguage: Api.Locale;
  storedLanguage: Api.Locale;
  pdApi: State.PdApi;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

const languagesFields = nameofFactory<Api.Locale>();

export type EditLanguageProps = StateProps & DispatchProps & OwnProps;

export type EditLocalizationStatusState = {
  localeNames: DropdownOption[];
};

export class EditLanguage extends React.Component<EditLanguageProps, EditLocalizationStatusState> {
  maxLocaleLength = 10;
  constructor(props: EditLanguageProps) {
    super(props);
    this.state = {
      localeNames: []
    };
  }

  public async componentDidMount(): Promise<void> {
    // Fetch localizations from PDAPI
    const url = `${pdApiUrl}/locales`;
    const response: void | AxiosResponse = await axios.get(url).catch(e => {
      this.setState(prevState => ({
        ...prevState,
        errorMessage: "Error fetching locales: " + e,
        fetchingAuxData: false
      }));
    });
    const localeNames: Api.LocaleName[] = (response as AxiosResponse)?.data || undefined;

    this.setState(prevstate => ({
      ...prevstate,
      localeNames: localeNames.map(
        locale =>
          ({
            text: locale.languageCode,
            value: locale.languageCode,
            key: locale.displayName
          } as DropdownOption)
      )
    }));
  }

  render(): JSX.Element {
    const { editedLanguage, storedLanguage, pdApi } = this.props;
    const isNew = !storedLanguage;
    const { locales } = pdApi;

    const remainingSet = this.state.localeNames.filter(itm =>
      locales.some(loc => loc.locale !== itm.text)
    );

    return (
      <Editor
        itemName={"language"}
        originalObject={storedLanguage}
        editedObject={editedLanguage}
        hasWritePermission={true} // todo
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("localizationids")}
        create={() => this.props.editActions.add("localizationids")}
      >
        <DropdownField
          id="locale"
          label="Locale"
          value={editedLanguage.locale}
          isError={this.isEmptyString(editedLanguage.locale) || !this.isFieldValid(languagesFields("locale"))}
          errorMessage ={this.isEmptyString(editedLanguage.locale) ? "The locale field is required." : 
            !this.isEmptyString(editedLanguage.locale) 
            && !this.isFieldValid(languagesFields("locale")) 
            && editedLanguage.locale.length <= this.maxLocaleLength ? "Friendly name is already created for this locale." : 
            !this.isEmptyString(editedLanguage.locale) 
            && editedLanguage.locale.length > this.maxLocaleLength ? "The Maximum number of characters of locale must not exceed 10." : ""
          }
          options={remainingSet}
          disabled={!isNew}
          onChange={locale =>
            this.props.editActions.setEdited({
              ...editedLanguage,
              locale: locale,
              friendlyName: this.state.localeNames.find(ln => ln.value === locale)?.key
            })
          }
        />
        <TextField
          id="friendlyName"
          label="Friendly Name"
          value={editedLanguage.friendlyName}
          isError={!this.isFieldValid(languagesFields("friendlyName"))}
          errorMessage={!this.isEmptyString(editedLanguage.friendlyName) ? "The Friendly name must be unique." : ""}
          onChange={friendlyName =>
            this.props.editActions.setEdited({ ...editedLanguage, friendlyName:  friendlyName.trim()})
          }
        />
        <TextField
          id="comment"
          label="Comment"
          value={editedLanguage.comment}
          isError={!this.isFieldValid(languagesFields("comment"))}
          onChange={comment => this.props.editActions.setEdited({ ...editedLanguage, comment })}
        />
        <CheckboxField
          id={"disabled"}
          label={"Disabled"}
          checked={editedLanguage.disabled}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedLanguage, disabled: value })
          }
          disabled={editedLanguage.locale === "en-US" ? true : false}
        />
      </Editor>
    );
  }
 
  private isEmptyString = (str: string): boolean => !(!!str && str.replace(/\s/g, "").length > 0);
  
  
  private isFieldValid = (field: string): boolean => {
    const { pdApi, editedLanguage, storedLanguage } = this.props;
    switch (field) {
      case languagesFields("locale"): {
        const localeExists = !!pdApi.locales.find(
          locale => locale.locale === editedLanguage.locale
        );
        const sameAsEdited = !!storedLanguage && editedLanguage.locale === storedLanguage.locale;
        const notEmpty = !!editedLanguage.locale;
        const notTooLong = editedLanguage.locale.length <= this.maxLocaleLength;

        return (!localeExists || sameAsEdited) && notEmpty && notTooLong;
      }
      case languagesFields("friendlyName"): {
        const friendlyNameGiven = !!editedLanguage.friendlyName;
        const friendlyNameNotReserved = pdApi.locales.find(
          locale =>
            locale.locale !== editedLanguage.locale &&
            locale.friendlyName.trim() === editedLanguage.friendlyName.trim()
        )
          ? false
          : true;
        return friendlyNameGiven && friendlyNameNotReserved;
      }
      case languagesFields("comment"): {
        const { comment } = editedLanguage;
        return !this.isEmptyString(comment);
      }
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const editedLocale = this.props.editedLanguage as Api.Locale;
    const temp = _.keys(editedLocale);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedLanguage: state.editView.editedObject,
  storedLanguage: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditLanguage);


